import { useState, useMemo, useEffect } from 'react';
import { Search, X, Plus, Check } from 'lucide-react';
import { useMyItems } from '@/hooks/useUserItems';
import { UserItem } from '@/types/userItem';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatUsdCents } from '@/lib/formatMoney';

interface InventorySearchPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect?: (item: UserItem) => void;
  onSelectMany?: (items: UserItem[]) => void;
  multiSelect?: boolean;
  excludeIds?: string[];
  trigger?: React.ReactNode;
  placeholder?: string;
}

export function InventorySearchPopover({
  open,
  onOpenChange,
  onSelect,
  onSelectMany,
  multiSelect = false,
  excludeIds = [],
  trigger,
  placeholder = "Search your inventory...",
}: InventorySearchPopoverProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { data: items = [], isLoading } = useMyItems();

  // Reset selection whenever popover opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setSearchQuery('');
    }
  }, [open]);

  // Filter to unsold items not in exclude list
  const filteredItems = useMemo(() => {
    const unsoldItems = items.filter(item =>
      !item.sold_at &&
      !excludeIds.includes(item.id) &&
      item.quantity > 0
    );

    if (!searchQuery.trim()) return unsoldItems.slice(0, 20);

    const query = searchQuery.toLowerCase();
    return unsoldItems.filter(item =>
      item.title.toLowerCase().includes(query) ||
      item.set_name?.toLowerCase().includes(query) ||
      item.set_code?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    ).slice(0, 20);
  }, [items, excludeIds, searchQuery]);

  const handleSingleSelect = (item: UserItem) => {
    onSelect?.(item);
    setSearchQuery('');
    onOpenChange(false);
  };

  const toggle = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddSelected = () => {
    if (selectedIds.size === 0) return;
    const picked = items.filter(i => selectedIds.has(i.id));
    onSelectMany?.(picked);
    setSelectedIds(new Set());
    setSearchQuery('');
    onOpenChange(false);
  };

  const defaultTrigger = (
    <Button
      type="button"
      variant="outline"
      className="w-full justify-start text-muted-foreground bg-black/20 backdrop-blur-md border-white/[0.08] hover:border-white/20"
    >
      <Plus className="w-4 h-4 mr-2" />
      Add from Inventory
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 bg-chrome border-white/[0.08]"
        align="start"
        sideOffset={4}
      >
        {/* Search input */}
        <div className="p-3 border-b border-white/[0.08]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10"
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading inventory...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {searchQuery ? 'No items found' : 'No items in inventory'}
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {filteredItems.map((item) => {
                const imageUrl = item.images?.[0]?.image_url || item.stock_image_url;
                const costDisplay = item.cost_basis_cents
                  ? `${formatUsdCents(item.cost_basis_cents)}`
                  : null;
                const checked = selectedIds.has(item.id);

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => multiSelect ? toggle(item.id) : handleSingleSelect(item)}
                    aria-pressed={multiSelect ? checked : undefined}
                    className={cn(
                      "w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors",
                      checked ? "bg-active-gold/10" : "hover:bg-white/[0.06]"
                    )}
                  >
                    {multiSelect && (
                      <span
                        className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors",
                          checked
                            ? "bg-active-gold border-active-gold"
                            : "border-white/20 bg-transparent"
                        )}
                      >
                        {checked && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                      </span>
                    )}

                    {/* Thumbnail */}
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={item.title}
                        className="w-10 h-10 rounded object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-white/[0.05] flex items-center justify-center shrink-0">
                        <span className="text-xs text-muted-foreground">N/A</span>
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="capitalize">{item.condition?.replace('_', ' ')}</span>
                        {item.quantity > 1 && (
                          <span className="text-primary">×{item.quantity}</span>
                        )}
                      </div>
                    </div>

                    {/* Cost */}
                    {costDisplay && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {costDisplay}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {multiSelect && (
          <div className="p-2 border-t border-white/[0.08] flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground pl-1">
              {selectedIds.size} selected
            </span>
            <Button
              type="button"
              size="sm"
              onClick={handleAddSelected}
              disabled={selectedIds.size === 0}
              className="h-8"
            >
              Add {selectedIds.size > 0 ? selectedIds.size : ''}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
